import { inviteLookupOptions } from '@doneverse/data/queries'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { AlertCircle, Loader2 } from 'lucide-react'
import { FormEvent, useEffect, useState } from 'react'
import styles from './Index.module.css'
import { AuthSplitLayout } from '@/components/auth/AuthSplitLayout'
import { RouteErrorBoundary } from '@/components/error/RouteErrorBoundary'
import { useAuth } from '@/contexts/AuthContext'
import { getHomeRoute } from '@/lib/routes'

export default function Signup() {
  const {
    isAuthenticated,
    loginWithRedirect,
    signupWithPassword,
    loading: authLoading,
    isLoggingOut,
    authError,
    logout,
  } = useAuth()
  const navigate = useNavigate()
  const { invite } = useSearch({ from: '/signup' })
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const {
    data: inviteData,
    isPending: isInviteQueryPending,
    isError: isInviteLookupFailed,
    error: inviteLookupError,
  } = useQuery(inviteLookupOptions(invite))
  const isInviteLookupPending = !!invite && isInviteQueryPending

  useEffect(() => {
    if (inviteData?.email) setEmail(inviteData.email)
  }, [inviteData])

  useEffect(() => {
    if (!invite && !authLoading && !isLoggingOut) {
      void navigate({ to: '/', replace: true })
    }
  }, [authLoading, invite, isLoggingOut, navigate])

  if (authLoading || isLoggingOut) {
    return (
      <div className="flex justify-center items-center h-dvh" data-testid="auth-loading">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  // No invite token — the effect above redirects to login; render nothing meanwhile
  if (!invite) {
    return null
  }

  if (isAuthenticated) {
    return (
      <RouteErrorBoundary
        error={
          new Error(
            authError || 'You are currently signed in. Sign out to accept this invite with another account.',
          )
        }
        title="Sign Out To Continue"
        retryLabel="Sign Out"
        onRetry={() => void logout({ returnTo: `/signup?invite=${invite}` }).catch(() => {})}
      />
    )
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setFormError(null)

    if (password !== confirmPassword) {
      setFormError('Passwords do not match')
      return
    }

    setIsSubmitting(true)
    const { error } = await signupWithPassword(email, password)

    if (error) {
      setFormError(error.message)
      setIsSubmitting(false)
      return
    }

    void navigate({ to: getHomeRoute(inviteData?.role), replace: true })
  }

  const isEmailLocked = !isInviteLookupPending && !isInviteLookupFailed && !!email
  const displayError =
    formError ??
    (isInviteLookupFailed ? (inviteLookupError?.message ?? 'Failed to load invite details') : null)
  const submitLabel = isInviteLookupPending
    ? 'Loading Invite...'
    : isSubmitting
      ? 'Creating Account...'
      : 'Create Account'

  return (
    <AuthSplitLayout
      heading="Welcome to Doneverse"
      subtitle="Create your account to get started"
      onGoogleClick={() =>
        loginWithRedirect({
          authorizationParams: { connection: 'google-oauth2' },
        })
      }
    >
      <>
        <form onSubmit={handleSubmit} className={styles.form}>
          <input
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            className={styles.input}
            placeholder="Email ID"
            aria-label="Email ID"
            disabled={isSubmitting || isInviteLookupPending || isInviteLookupFailed}
            readOnly={isEmailLocked}
            aria-readonly={isEmailLocked}
            required
          />

          <input
            type="password"
            name="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            className={styles.input}
            placeholder="Create Password"
            aria-label="Create Password"
            disabled={isSubmitting}
            required
          />

          <input
            type="password"
            name="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            className={styles.input}
            placeholder="Confirm Password"
            aria-label="Confirm Password"
            disabled={isSubmitting}
            required
          />
          <p className={styles.formHint}>
            {isInviteLookupPending
              ? 'Loading your invited email...'
              : 'Use the email address your invite was sent to.'}
          </p>

          <button
            className={`${styles.loginButton} ${styles.loginSubmitButton}`}
            type="submit"
            disabled={isSubmitting || isInviteLookupPending || isInviteLookupFailed}
          >
            {submitLabel}
          </button>
        </form>

        {displayError && (
          <div className={styles.errorCard} role="alert">
            <div className={styles.errorTitle}>
              <AlertCircle className="h-4 w-4" />
              <span>Signup Error</span>
            </div>
            <p className={styles.errorMessage}>{displayError}</p>
          </div>
        )}
      </>
    </AuthSplitLayout>
  )
}
